import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useJobApplicationStore } from '@core/jobApplication/useJobApplicationStore';
import {
  Header,
  Jobs,
  Applications,
  Activity,
  Details,
  Documents,
} from '../components/worker';
import Breadcrumbs from '../components/Breadcrumbs';
import Tabs from '../components/Tabs';
import { Spinner } from '../components/shared/Spinner';

// Define worker tabs
const workerTabs = [
  { key: 'details', label: 'Details' },
  { key: 'documents', label: 'Documents' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'applications', label: 'Applications' },
  { key: 'activity', label: 'Activity' },
];

const WorkerPage = () => {
  const { id } = useParams<{ id: string }>();
  const { fetchWorkerById, getWorkerById } = useWorkerStore();
  const worker = getWorkerById(id ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] =
    useState<(typeof workerTabs)[number]['key']>('details');
  const { fetchJobApplicationsForUser } = useJobApplicationStore();

  const handleLogoUpdate = async (newLogoUrl: string) => {
    if (id) {
      await fetchWorkerById(id);
    }
  };

  useEffect(() => {
    const fetchWorker = async () => {
      try {
        setIsLoading(true);
        if (id) {
          await fetchWorkerById(id);
        }
      } catch (error) {
        console.error(`Error fetching worker: ${error}`);
        setError(error instanceof Error ? error.message : 'Error');
        toast.error(
          `Fetching worker failed: ${error instanceof Error ? error.message : 'Error'}`,
          {
            position: 'bottom-left',
            autoClose: 5000,
            toastId: 'fetch-worker-error',
          },
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorker();
  }, [id, fetchWorkerById]);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        if (worker?.id) {
          await fetchJobApplicationsForUser(worker.id);
        }
      } catch (error) {
        console.error(`Error getting job applications: ${error}`);
        toast.error(
          `Fetching job applications failed: ${error instanceof Error ? error.message : 'Error'}`,
          {
            position: 'bottom-left',
            autoClose: 5000,
            toastId: 'fetch-applications-error',
          },
        );
      }
    };

    fetchApplications();
  }, [worker, fetchJobApplicationsForUser]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="animate-pulse">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !worker) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-600">{error || 'Worker not found'}</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Breadcrumbs
        items={[
          { label: 'Workers', to: '/workers' },
          {
            label: worker ? `${worker.firstName} ${worker.lastName}` : 'Worker',
          },
        ]}
        className="mb-4"
      />
      <Header
        id={worker.id}
        name={`${worker.firstName} ${worker.lastName}`}
        logoUrl={worker.profilePictureUrl ?? undefined}
        status={worker.status}
        onLogoUpdate={handleLogoUpdate}
      />
      <Tabs
        tabs={workerTabs}
        selectedTabKey={selectedTab}
        onTabChange={(key) => setSelectedTab(key as typeof selectedTab)}
      >
        {selectedTab === 'details' && <Details worker={worker} />}
        {selectedTab === 'documents' && <Documents worker={worker} />}
        {selectedTab === 'jobs' && <Jobs worker={worker} />}
        {selectedTab === 'activity' && <Activity workerId={worker.id} />}
        {selectedTab === 'applications' && <Applications worker={worker} />}
      </Tabs>
    </div>
  );
};

export default WorkerPage;
